import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MassOsItinerary } from 'src/entities/file';

@Component({
  selector: 'app-mass-os-itineraries-table',
  templateUrl: './mass-os-itineraries-table.component.html',
  styleUrls: ['./mass-os-itineraries-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MassOsItinerariesTableComponent {
  @Input() itineraries: MassOsItinerary[] = [];

  /** Estado del paginador: el padre (mass-os-generation) obtiene cada página. */
  @Input() pageIndex = 0;
  @Input() pageSize = 10;
  @Input() paginatorLength = 0;

  /** Muestra el banner de carga al navegar por el paginador / precargar servicios. */
  @Input() isLoading = false;

  @Output() pageChange = new EventEmitter<PageEvent>();

  @Output() selectionChanged = new EventEmitter<number>();

  /** Cantidad de tickets seleccionados (para el resumen y contador). */
  @Output() ticketSelectionChanged = new EventEmitter<number>();

  /** Patrón Ticketería: expand primero y un solo itinerario abierto a la vez. */
  readonly displayedColumns = ['Check', 'composite'];

  readonly columnsToDisplayWithExpand = [
    'expand',
    ...this.displayedColumns
  ];

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** Selección para OS a nivel servicio; el padre refleja el agregado. */
  private selectedServiceIds = new Set<string>();

  /** Tickets seleccionados (crear OS por ticket) por servicio (serviceId → ticketIds). */
  private selectedTicketIds = new Map<string, Set<string>>();

  /** Itinerarios expandidos (soporta abrir varios a la vez). */
  private expandedItineraryIds = new Set<string>();

  /** Trigger para que las sub-tablas de servicios expandan todos sus TICKET. */
  expandAllTickets = 0;

  get selectedIdList(): string[] {
    return Array.from(this.selectedServiceIds);
  }

  get selectedCount(): number {
    return this.selectedServiceIds.size;
  }

  /** Cantidad de tickets seleccionados (para mostrar en el toolbar). */
  get selectedTicketCount(): number {
    return Array.from(this.selectedTicketIds.values()).reduce(
      (acc, set) => acc + set.size,
      0
    );
  }

  /** Mapa serviceId → ticketIds seleccionados (para la sub-tabla). */
  get selectedTickets(): Record<string, string[]> {
    const record: Record<string, string[]> = {};
    for (const [serviceId, set] of this.selectedTicketIds.entries()) {
      record[serviceId] = Array.from(set);
    }
    return record;
  }

  trackByItinerary = (_index: number, item: MassOsItinerary): string =>
    item.id;

  isExpanded(itinerary: MassOsItinerary): boolean {
    return this.expandedItineraryIds.has(itinerary.id);
  }

  /** Abre/cierra un itinerario; permite tener varios open a la vez. */
  toggleExpand(itinerary: MassOsItinerary): void {
    this.toggleItineraryExpanded(itinerary.id);
  }

  private toggleItineraryExpanded(id: string): void {
    if (this.expandedItineraryIds.has(id)) {
      this.expandedItineraryIds.delete(id);
    } else {
      this.expandedItineraryIds.add(id);
    }
    this.cdr.markForCheck();
  }

  get allExpanded(): boolean {
    return this.itineraries.length > 0 &&
      this.itineraries.every((it) => this.expandedItineraryIds.has(it.id));
  }

  /** Chevrón del toolbar: abre todos los itinerarios (+ sub-tablas TICKET). */
  toggleAllExpand(): void {
    if (this.allExpanded) {
      this.expandedItineraryIds.clear();
      this.expandAllTickets = 0;
    } else {
      for (const it of this.itineraries) {
        this.expandedItineraryIds.add(it.id);
      }
      this.expandAllTickets = this.expandAllTickets + 1;
    }
    this.cdr.markForCheck();
  }

  private itineraryServiceIds(itinerary: MassOsItinerary): string[] {
    if (Array.isArray(itinerary.serviceIds) && itinerary.serviceIds.length) {
      return itinerary.serviceIds;
    }
    return itinerary.servicios.map((servicio) => servicio.id);
  }

  /** Ids seleccionables para OS (excluye catálogo SSFF bloqueado). */
  private itinerarySelectableIds(itinerary: MassOsItinerary): string[] {
    return itinerary.servicios
      .filter((servicio) => servicio.displayTypeService !== 'ssff')
      .map((servicio) => servicio.id);
  }

  isItineraryChecked(itinerary: MassOsItinerary): boolean {
    const ids = this.itineraryServiceIds(itinerary);
    return (
      ids.length > 0 && ids.every((id) => this.selectedServiceIds.has(id))
    );
  }

  isItineraryIndeterminate(itinerary: MassOsItinerary): boolean {
    const ids = this.itineraryServiceIds(itinerary);
    const selected = ids.filter((id) => this.selectedServiceIds.has(id));
    return selected.length > 0 && selected.length < ids.length;
  }

  toggleItinerary(itinerary: MassOsItinerary, checked: boolean): void {
    this.applyItineraryToggle(this.itinerarySelectableIds(itinerary), checked);
  }

  private applyItineraryToggle(ids: string[], checked: boolean): void {
    const next = new Set(this.selectedServiceIds);
    for (const id of ids) {
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    this.selectedServiceIds = next;
    this.emitSelection();
  }

  isAllSelected(): boolean {
    const allIds = this.itineraries.flatMap((item) =>
      this.itineraryServiceIds(item)
    );
    return allIds.length > 0 && allIds.every((id) => this.selectedServiceIds.has(id));
  }

  isAllIndeterminate(): boolean {
    return this.selectedServiceIds.size > 0 && !this.isAllSelected();
  }

  toggleAll(): void {
    const allIds = this.itineraries.flatMap((item) =>
      this.itinerarySelectableIds(item)
    );
    const next = this.isAllSelected()
      ? new Set<string>()
      : new Set(allIds);
    this.selectedServiceIds = next;
    // Al seleccionar todo, abrir los files que tienen servicios TICKET para
    // que la sub-tabla haga el preload y permita marcar tickets.
    if (next.size > 0) {
      const tipoServiceChecker = (s: { tipoServicio?: string }) =>
        String(s?.tipoServicio ?? '').toUpperCase() === 'TICKET';
      for (const item of this.itineraries) {
        if ((item.servicios ?? []).some(tipoServiceChecker)) {
          this.expandedItineraryIds.add(item.id);
        }
      }
      this.expandAllTickets = this.expandAllTickets + 1;
    } else {
      this.expandedItineraryIds.clear();
      this.expandAllTickets = 0;
    }
    this.cdr.markForCheck();
    this.emitSelection();
  }

  onToggleService(event: { serviceId: string; checked: boolean }): void {
    const next = new Set(this.selectedServiceIds);
    if (event.checked) {
      next.add(event.serviceId);
    } else {
      next.delete(event.serviceId);
    }
    this.selectedServiceIds = next;
    this.emitSelection();
  }

  /**
   * Checkbox de un ticket: marca/desmarca el ticket y de paso selecciona el
   * servicio padre para que participe en la generación de OS.
   */
  onTicketToggle(event: {
    serviceId: string;
    ticketId: string;
    checked: boolean;
  }): void {
    const serviceSet = this.selectedTicketIds.get(event.serviceId) ?? new Set<string>();
    let changed = false;
    if (event.checked) {
      changed = !serviceSet.has(event.ticketId);
      serviceSet.add(event.ticketId);
    } else {
      changed = serviceSet.delete(event.ticketId);
    }
    if (changed) {
      this.selectedTicketIds.set(event.serviceId, serviceSet);
      // El servicio padre queda seleccionado mientras tenga tickets marcados.
      const serviceSelected = serviceSet.size > 0;
      const serviceIds = new Set(this.selectedServiceIds);
      if (serviceSelected) {
        serviceIds.add(event.serviceId);
      } else if (this.isServiceOnlyTicketSelection(event.serviceId)) {
        serviceIds.delete(event.serviceId);
      }
      this.selectedServiceIds = serviceIds;
      this.emitSelection();
    }
  }

  /** ¿Este servicio quedó deseleccionado salvo por sus tickets? (para limpiarlo). */
  private isServiceOnlyTicketSelection(serviceId: string): boolean {
    return !this.selectedServiceIds.has(serviceId);
  }

  private emitSelection(): void {
    this.selectionChanged.emit(this.selectedServiceIds.size);
    this.ticketSelectionChanged.emit(this.selectedTicketCount);
  }
}
